import React, { useCallback } from 'react';

import { SuggestAsync } from '.';
import { api } from '../../../../helpers/client';
import { ISuggestProps } from './types';

type INameSuggestProps<T> = Pick<ISuggestProps<T>, 'input' | 'onBlur' | 'onChange' | 'onFocus' | 'onSelect'>;

export type FullNameOption = {
    result: string;
    firstName: string;
    lastName: string;
    patronymic: string;
    gender?: 'male' | 'female';
    onlyManual?: boolean;
};

const getOptionFromValue = (value: string): FullNameOption => {
    const [lastName = '', firstName = '', patronymic = ''] = (value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\s*-\s*/g, '-')
        .replace(/(^|[\s-])[а-яё](?=[а-яё])/g, (letter) => letter.toUpperCase())
        .split(/\s+/);
    const result = [lastName, firstName, patronymic].filter((v) => v).join(' ');
    return { firstName, lastName, patronymic, result };
};

export function FullNameSuggest(props: INameSuggestProps<FullNameOption>) {
    const { onSelect, onBlur, ...other } = props;

    const onBlurHandler = useCallback(
        (event: FE.FieldChangeEvent, item?: FullNameOption) => {
            const {
                target: { value },
            } = event;
            const raw = getOptionFromValue(value);
            const {
                firstName = raw.firstName,
                lastName = raw.lastName,
                patronymic = raw.patronymic,
                gender,
            } = item || {};
            const result = [lastName, firstName, patronymic].filter((v) => v).join(' ');
            const currentOption: FullNameOption = { firstName, lastName, patronymic, result, gender };
            if (!item && onSelect) {
                onSelect(currentOption);
            }
            const currentValue = currentOption.result || value;
            return onBlur && onBlur({ target: { ...event.target, value: currentValue } }, item);
        },
        [onBlur, onSelect],
    );

    const onSelectHandler = useCallback(
        (option: FullNameOption) => {
            const normalized = {
                ...option,
                result: `${option.lastName} ${option.firstName} ${option.patronymic}`.trim(),
            };
            return onSelect && onSelect(normalized);
        },
        [onSelect],
    );

    const getOptions = useCallback(async (value) => {
        const source =
            value &&
            value
                .trim()
                .replace(/\s+/g, ' ')
                .replace(/\s*-\s*/, '-')
                .split(' ')
                .slice(0, 4)
                .join(' ');

        const newNames = source ? await api.fullNameSuggest(source) : [];
        return newNames.map(
            ({ value: result, data }) =>
                ({
                    result,
                    firstName: data.name || '',
                    lastName: data.surname || '',
                    patronymic: data.patronymic || '',
                    gender: data.gender === 'UNKNOWN' ? undefined : data.gender.toLowerCase(),
                } as FullNameOption),
        );
    }, []);

    return (
        <SuggestAsync<FullNameOption>
            {...other}
            getOptions={getOptions}
            onBlur={onBlurHandler}
            onSelect={onSelectHandler}
            onlyList
            onlyRus
            input={props.input}
            mapperOption={(option) => ({
                name: props?.input?.name || '',
                value: option.result,
                onlyManual: option.onlyManual,
                option,
            })}
        />
    );
}
